import { type FC, type ComponentProps, useCallback, type ReactNode } from 'react';
import { useFragment, graphql, usePaginationFragment } from 'react-relay';

import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    triggerConstants,
} from 'dibs-tracking';
import { flattenConnectionNodes } from 'dibs-ts-utils/exports/flattenConnectionNodes.ts';

import { HpSharedItemTilesCarouselModule } from '../HpSharedItemTilesCarouselModule/HpSharedItemTilesCarouselModule.tsx';

import { type HpSharedBestsellingDesignsCarousel_viewer$key } from './__generated__/HpSharedBestsellingDesignsCarousel_viewer.graphql.ts';
import { type HpSharedBestsellingDesignsCarousel_user$key } from './__generated__/HpSharedBestsellingDesignsCarousel_user.graphql.ts';

const viewerFragment = graphql`
    fragment HpSharedBestsellingDesignsCarousel_viewer on Viewer
    @refetchable(queryName: "HpSharedBestsellingDesignsCarouselPaginationQuery")
    @argumentDefinitions(count: { type: "Int", defaultValue: 6 }, cursor: { type: "String" }) {
        bestsellingDesigns: itemSearchTrending(
            first: $count
            after: $cursor
            module: BEST_SELLING_MODULE
        ) @connection(key: "HpSharedBestsellingDesignsCarousel_bestsellingDesigns", filters: []) {
            # eslint-disable-next-line relay/unused-fields
            edges {
                node {
                    ...HpSharedItemTilesCarouselModule_items
                        @arguments(
                            fetchFavorites: $hasUserId
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
            totalResults
        }
        ...HpSharedItemTilesCarouselModule_viewer
            @arguments(fetchFavorites: $hasUserId, userId: $userId)
    }
`;

const userFragment = graphql`
    fragment HpSharedBestsellingDesignsCarousel_user on User {
        ...HpSharedItemTilesCarouselModule_user
    }
`;

export const HpSharedBestsellingDesignsCarousel: FC<{
    viewer?: HpSharedBestsellingDesignsCarousel_viewer$key | null;
    user?: HpSharedBestsellingDesignsCarousel_user$key | null;
    isMobile: boolean;
    title?: ReactNode;
    viewMoreLink?: string | null;
    addTopGap?: boolean;
}> = ({ viewer: viewerRef, user: userRef, isMobile, title, viewMoreLink, addTopGap }) => {
    const { data: viewer, loadNext } = usePaginationFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const onArrowClick: ComponentProps<typeof HpSharedItemTilesCarouselModule>['onArrowClick'] =
        useCallback(() => {
            trackEvent({
                eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                interaction_type: interactionTypeConstants.INTERACTION_TYPE_HOMEPAGE,
                step_interaction_name:
                    stepInteractionConstants.STEP_INTERACTION_BESTSELLERS_ARROW_CLICK,
                trigger: triggerConstants.TRIGGER_BESTSELLERS,
            });
        }, []);

    const { bestsellingDesigns } = viewer || {};
    const items = flattenConnectionNodes(bestsellingDesigns);
    const totalResults = bestsellingDesigns?.totalResults || 0;

    return (
        <HpSharedItemTilesCarouselModule
            viewer={viewer}
            user={user}
            items={items}
            isMobile={isMobile}
            totalResults={totalResults}
            trackingList={triggerConstants.TRIGGER_BESTSELLERS}
            loadNext={loadNext}
            onArrowClick={onArrowClick}
            title={title}
            viewMoreLink={viewMoreLink}
            dataTn="excellent-values"
            addTopGap={addTopGap}
        />
    );
};
